import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { logWarn } from '@/shared/lib/logging';
import { RecaptchaContext } from '@/shared/lib/recaptcha';

interface RecaptchaContextType {
    isLoaded: boolean;
    isLoading: boolean;
    error: string | null;
}

interface RecaptchaProviderProps extends PropsWithChildren {
    siteKey?: string;
}

// Global window extensions
declare global {
    interface Window {
        // Google reCAPTCHA
        grecaptcha: {
            ready: (cb: () => void) => void;
            execute: (
                siteKey: string,
                options: { action: string },
            ) => Promise<string>;
        };
    }
}

export function RecaptchaProvider(props: RecaptchaProviderProps) {
    const { children, siteKey } = props;

    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const recaptchaSiteKey =
        siteKey ||
        (typeof process !== 'undefined'
            ? process.env.ALGOTECH_RECAPTCHA_SITE_KEY
            : undefined);

    useEffect(() => {
        if (!recaptchaSiteKey) {
            if (__IS_DEV__) {
                // eslint-disable-next-line no-console
                console.warn('ALGOTECH_RECAPTCHA_SITE_KEY not configured');
            }

            setError('reCAPTCHA site key not configured');

            return undefined;
        }

        // Check if reCAPTCHA is already loaded
        if (window.grecaptcha) {
            setIsLoaded(true);

            return undefined;
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector(
            'script[src*="google.com/recaptcha/api.js"]',
        );

        if (existingScript) {
            setIsLoading(true);
            existingScript.addEventListener('load', () => {
                setIsLoaded(true);
                setIsLoading(false);
            });
            existingScript.addEventListener('error', () => {
                setError('Failed to load reCAPTCHA script');
                setIsLoading(false);
            });

            return undefined;
        }

        // Load reCAPTCHA script
        setIsLoading(true);
        const script = document.createElement('script');

        script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
            setIsLoaded(true);
            setIsLoading(false);
        };

        script.onerror = () => {
            setError('Failed to load reCAPTCHA script');
            setIsLoading(false);
        };

        document.head.appendChild(script);

        return () => {
            // Cleanup on unmount
            const scriptElement = document.querySelector(
                'script[src*="google.com/recaptcha/api.js"]',
            );

            if (scriptElement && scriptElement.parentNode === document.head) {
                try {
                    document.head.removeChild(scriptElement);
                } catch (err) {
                    logWarn('Failed to remove reCAPTCHA script:', err);
                }
            }
        };
    }, [recaptchaSiteKey]);

    const contextValue: RecaptchaContextType = useMemo(
        () => ({
            isLoaded,
            isLoading,
            error,
        }),
        [isLoaded, isLoading, error],
    );

    return (
        <RecaptchaContext.Provider value={contextValue}>
            {children}
        </RecaptchaContext.Provider>
    );
}
